import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { Box, IconButton, TextField, Tooltip } from "@mui/material";
import { useFormikContext } from "formik";
import type { AddSolutionFormValues } from "../../../add/form/types";
import type { EditSolutionFormValues } from "../../../edit/form/types";
import type {
  SatelliteImageFieldName,
  SatelliteImageInputProps,
} from "../../types";

function SatelliteImage({ value, index, remove }: SatelliteImageInputProps) {
  const context = useFormikContext<
    AddSolutionFormValues | EditSolutionFormValues
  >();

  const getValidationMessage = (
    fieldName: SatelliteImageFieldName,
  ): string | undefined => {
    const touchedRow = context.touched["satellite-imagery"]?.[index];
    const errorsRow = context.errors["satellite-imagery"]?.[index];
    const pairTouched = Boolean(touchedRow?.mediaLink || touchedRow?.caption);

    if (!pairTouched) return undefined;
    if (!errorsRow || typeof errorsRow === "string") return undefined;

    const fieldError = errorsRow[fieldName];
    return typeof fieldError === "string" ? fieldError : undefined;
  };

  return (
    <Box
      data-testid={`satellite-image-container-${value.satelliteImageCounter}`}
      sx={{
        display: "flex",
        direction: "column",
        justifyContent: "space-between",
      }}
    >
      <Box
        data-testid={`satellite-image-fields-${value.satelliteImageCounter}`}
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "90%",
        }}
      >
        {(["mediaLink", "caption"] as const).map((fieldName) => {
          const validationMessage = getValidationMessage(fieldName);
          const label = fieldName === "mediaLink" ? "Media Link" : "Caption";
          const fieldPath = `satellite-imagery[${index}].${fieldName}`;

          return (
            <TextField
              key={fieldName}
              id={`satellite-image-${fieldName}-${value.satelliteImageCounter}`}
              name={fieldPath}
              label={label}
              variant="standard"
              fullWidth
              sx={{ paddingBottom: 2 }}
              autoComplete="disabled"
              value={
                context.values["satellite-imagery"][index]?.[fieldName] ?? ""
              }
              onChange={context.handleChange}
              onBlur={() => context.setFieldTouched(fieldPath, true, true)}
              error={Boolean(validationMessage)}
              helperText={validationMessage}
            />
          );
        })}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Tooltip title="Delete satellite image">
          <IconButton
            onClick={remove}
            sx={{ padding: 0 }}
            aria-label={`delete-satellite-image-${value.satelliteImageCounter}`}
            data-testid={`delete-satellite-image-${value.satelliteImageCounter}`}
            size="large"
          >
            <DeleteForeverIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

export default SatelliteImage;
